import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Loader, 
  Search, 
  CheckCircle, 
  XCircle, 
  ChevronLeft, 
  ChevronRight,
  Save,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../context/NotificationContext';
import { format, addDays, parseISO, isValid } from 'date-fns';

interface TimeSlot {
  id: string;
  date: string;
  time_slot: string;
  is_available: boolean;
  booking_id: string | null;
}

export default function TimeSlots() {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [workingHours, setWorkingHours] = useState({
    start: '08:00',
    end: '17:00'
  });
  const [slotDuration, setSlotDuration] = useState(30);

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchTimeSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchSystemSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('working_hours_start, working_hours_end, service_buffer_minutes')
        .single();

      if (error) throw error;

      if (data) {
        setWorkingHours({
          start: data.working_hours_start,
          end: data.working_hours_end
        });
        setSlotDuration(data.service_buffer_minutes);
      }
    } catch (error) {
      console.error('Error fetching system settings:', error);
      showNotification('error', 'Failed to load system settings');
    }
  };

  const fetchTimeSlots = async (date: string) => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-time-slots?date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch time slots');
      }

      const slots = await response.json();
      setTimeSlots(slots);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      showNotification('error', 'Failed to load time slots');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    if (isValid(parseISO(newDate))) {
      setSelectedDate(newDate);
    }
  };

  const handlePreviousDay = () => {
    const currentDate = parseISO(selectedDate);
    const previousDay = addDays(currentDate, -1);
    setSelectedDate(format(previousDay, 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const currentDate = parseISO(selectedDate);
    const nextDay = addDays(currentDate, 1);
    setSelectedDate(format(nextDay, 'yyyy-MM-dd'));
  };

  const toggleSlotAvailability = (slotId: string) => {
    setTimeSlots(slots =>
      slots.map(slot =>
        slot.id === slotId
          ? { ...slot, is_available: !slot.is_available }
          : slot
      )
    );
  };

  const saveChanges = async () => {
    try {
      setSaving(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-time-slots`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ slots: timeSlots }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save time slots');
      }
      
      showNotification('success', 'Time slots updated successfully');
    } catch (error) {
      console.error('Error saving time slots:', error);
      showNotification('error', 'Failed to save time slots');
    } finally {
      setSaving(false);
    }
  };

  const regenerateSlots = async () => {
    try {
      setRefreshing(true);
      await fetchTimeSlots(selectedDate);
      showNotification('success', 'Time slots regenerated successfully');
    } catch (error) {
      console.error('Error regenerating time slots:', error);
      showNotification('error', 'Failed to regenerate time slots');
    } finally {
      setRefreshing(false);
    }
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Time Slot Management</h1>
        <div className="flex gap-2">
          <button
            onClick={regenerateSlots}
            disabled={refreshing}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {refreshing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Regenerate Slots
              </>
            )}
          </button>
          <button
            onClick={saveChanges}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousDay}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleNextDay}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-sm">Unavailable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
              <span className="text-sm">Booked</span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-6">
            Time Slots for {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
          </h2>
          
          {timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No time slots found for this date
              </p>
              <button
                onClick={regenerateSlots}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Generate Time Slots
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {timeSlots.map((slot) => (
                <div
                  key={slot.id}
                  onClick={() => slot.booking_id ? null : toggleSlotAvailability(slot.id)}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-colors ${
                    slot.booking_id
                      ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed'
                      : slot.is_available
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
                  }`}
                >
                  <div className="flex items-center justify-center mb-2">
                    <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <p className="font-medium">
                    {formatTime(slot.time_slot)}
                  </p>
                  <div className="mt-2">
                    {slot.booking_id ? (
                      <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                        Booked
                      </span>
                    ) : slot.is_available ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}